import { Alert } from 'components/Alert';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import { Formik } from 'formik';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import Encryption from 'services/Encryption';
import Logger, { LoggingModule } from 'services/Logger';
import { Color } from 'modules/styles/colors';
import { FontSize } from 'modules/styles/variables';
import { ProfileUpdateMessages } from 'store/profile/constants';
import * as appSettingsSelectors from 'store/appSettings/selectors';
import { Insurance } from 'store/findProvider/types';
import { getProfile, updateConsumerSSN } from 'store/profile/actions';
import * as profileSelectors from 'store/profile/selectors';
import { RootDispatch, RootState } from 'store/types';
import {
  MuiBox,
  MuiButton,
  MuiContainer,
  MuiPaper,
  MuiTextField,
  MuiTypography
} from 'theme/material-ui';
import * as Yup from 'yup';
import { UPDATE_DISCLAIMER } from './variables';

interface EditSocialSecurityProps extends RouteComponentProps {
  selectHealthInsurance: Insurance;
  encryptionSettings: {
    password: string;
    algorithm: string;
  };
  dispatch: RootDispatch;
}

const editSSNSchema = Yup.object().shape({
  ssn: Yup.string()
    .matches(/^[0-9]{9}$/, 'A valid Social Security Number is required')
    .required('Social Security Number is required')
});

const initialValues = {
  ssn: ''
};

export function EditSocialSecurity({
  history,
  selectHealthInsurance,
  encryptionSettings,
  dispatch
}: EditSocialSecurityProps) {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleSuccess = () => history.goBack();

  const handleCancel = () => history.goBack();

  const onSubmit = async ({ ssn }: { ssn: string }) => {
    const { subscriber } = selectHealthInsurance;

    if (!subscriber) {
      return Alert.alert('Subscriber Info not found');
    }

    const { consumerId, subscriberId, suffix } = subscriber;
    const encryptedSsn = Encryption.encrypt(
      encryptionSettings.algorithm,
      encryptionSettings.password,
      ssn
    );

    setIsLoading(true);

    const res = await dispatch(updateConsumerSSN(consumerId, subscriberId, suffix, encryptedSsn));

    setIsLoading(false);

    if (res.error) {
      Logger.error(`${LoggingModule.profile}Failed to add SSN`, res.error);

      return Alert.alert(
        ProfileUpdateMessages.UPDATE_SSN.title,
        ProfileUpdateMessages.UPDATE_SSN.message
      );
    }

    dispatch(getProfile());
    return Alert.alert('Successfully added SSN', UPDATE_DISCLAIMER, [
      { text: 'OK', onPress: handleSuccess }
    ]);
  };

  return (
    <MuiBox mt={6}>
      <SpinnerModal isLoading={isLoading} />

      <MuiContainer maxWidth="sm">
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={editSSNSchema}>
          {({ touched, errors, values, handleChange, handleBlur, isValid, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <MuiBox p={3} component={MuiPaper}>
                <MuiBox mb={2}>
                  <MuiTypography
                    fontWeight={600}
                    fontSize={FontSize.large}
                    color={Color.textLight}
                    gutterBottom
                  >
                    Add SSN
                  </MuiTypography>
                </MuiBox>

                <MuiBox mb={3}>
                  <MuiTextField
                    type="text"
                    name="ssn"
                    fullWidth
                    placeholder="Enter SSN"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.ssn}
                    error={Boolean(touched.ssn && errors.ssn)}
                    helperText={touched.ssn && errors.ssn ? errors.ssn : null}
                  />
                </MuiBox>

                <MuiBox mb={1}>
                  <MuiButton
                    fullWidth
                    variant="outlined"
                    disabled={!isValid}
                    onClick={handleCancel}
                  >
                    Cancel
                  </MuiButton>
                </MuiBox>

                <MuiButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={!isValid}
                  type="submit"
                >
                  Submit
                </MuiButton>
              </MuiBox>
            </form>
          )}
        </Formik>
      </MuiContainer>
    </MuiBox>
  );
}

const mapState = (state: RootState) => ({
  consumer: profileSelectors.profileConsumerSelector(state),
  encryptionSettings: appSettingsSelectors.encryptionSettingsSelector(state),
  selectHealthInsurance: profileSelectors.selectHealthFacetsInsuranceSelector(state)
});

export default connect(mapState)(EditSocialSecurity);
